<script lang="ts">
    import { goto } from '$app/navigation';
    import { format } from 'date-fns';
    import { ja } from 'date-fns/locale';
    import type { PageData } from './$types';
    import { Alert, AlertDescription } from '$lib/components/shadcn/ui/alert';
    import { Badge } from '$lib/components/shadcn/ui/badge';
    import { Card, CardContent, CardHeader, CardTitle } from '$lib/components/shadcn/ui/card';
    import { Calendar, Users } from 'lucide-svelte';

    export let data: PageData;

    // デバッグ用
    console.log('Events data:', data);

    function formatEventDate(dateString: string): string {
        return format(new Date(dateString), 'yyyy年M月d日(E)', { locale: ja });
    }

    function formatEventPeriod(startDate: string, endDate: string): string {
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (start.toDateString() === end.toDateString()) {
            return format(start, 'yyyy年M月d日(E)', { locale: ja });
        }
        
        return `${format(start, 'yyyy年M月d日(E)', { locale: ja })} ～ ${format(end, 'M月d日(E)', { locale: ja })}`;
    }

    function getEventStatus(startDate: string, endDate: string): 'upcoming' | 'ongoing' | 'ended' {
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (now < start) return 'upcoming';
        if (now > end) return 'ended';
        return 'ongoing';
    }

    function handleEventClick(eventId: number) {
        console.log('Event clicked:', eventId);
        goto(`/events/${eventId}`);
    }
</script>

<svelte:head>
    <title>イベント一覧 - YT Grid</title>
    <meta name="description" content="VTuberのイベント・配信企画の一覧をご覧いただけます。" />
</svelte:head>

<div class="container mx-auto py-8 px-4">
    <!-- ヘッダー -->
    <div class="mb-8 text-center">
        <h1 class="text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
            イベント一覧
        </h1>
        <p class="text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
            VTuberの特別企画やイベント配信をまとめてチェックできます。
        </p>
    </div>

    {#if data.error}
        <Alert variant="destructive" class="mb-6">
            <AlertDescription>{data.error}</AlertDescription>
        </Alert>
    {:else if data.events.length === 0}
        <div class="text-center py-12">
            <Calendar class="h-16 w-16 text-neutral-400 mx-auto mb-4" />
            <h2 class="text-xl font-semibold text-neutral-600 dark:text-neutral-400 mb-2">
                開催中のイベントはありません
            </h2>
            <p class="text-neutral-500 dark:text-neutral-500">
                新しいイベントが開始されるまでお待ちください。
            </p>
        </div>
    {:else}
        <!-- イベント一覧 -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {#each data.events as event (event.id)}
                {@const status = getEventStatus(event.start_date, event.end_date)}
                <div
                    class="cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
                    on:click={() => handleEventClick(event.id)}
                    tabindex={0}
                    role="button"
                    aria-label={`${event.name}の詳細を表示`}
                >
                    <Card>
                        {#if event.image_url}
                            <div class="relative w-full h-48 overflow-hidden rounded-t-lg">
                                <img 
                                    src={event.image_url} 
                                    alt={event.name}
                                    class="w-full h-full object-cover"
                                    loading="lazy"
                                />
                                <div class="absolute top-3 right-3">
                                    <Badge variant={status === 'ongoing' ? 'default' : status === 'upcoming' ? 'secondary' : 'outline'}>
                                        {status === 'ongoing' ? '開催中' : status === 'upcoming' ? '開催予定' : '終了'}
                                    </Badge>
                                </div>
                            </div>
                        {/if}
                        
                        <CardHeader class="pb-3">
                            <div class="flex items-start justify-between gap-3">
                                <CardTitle class="text-lg line-clamp-2 leading-tight">
                                    {event.name}
                                    <span class="text-xs text-gray-500">(ID: {event.id})</span>
                                </CardTitle>
                                {#if !event.image_url}
                                    <Badge variant={status === 'ongoing' ? 'default' : status === 'upcoming' ? 'secondary' : 'outline'}>
                                        {status === 'ongoing' ? '開催中' : status === 'upcoming' ? '開催予定' : '終了'}
                                    </Badge>
                                {/if}
                            </div>
                            
                            <div class="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                                <Calendar class="h-4 w-4" />
                                <span>
                                    {formatEventPeriod(event.start_date, event.end_date)}
                                </span>
                            </div>
                        </CardHeader>
                        
                        <CardContent>
                            {#if event.description}
                                <p class="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-3 leading-relaxed">
                                    {event.description}
                                </p>
                            {/if}
                            
                            <div class="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                                <div class="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-500">
                                    <span class="flex items-center gap-1">
                                        <Users class="h-3 w-3" />
                                        詳細を見る
                                    </span>
                                    <span>
                                        {status === 'ongoing' ? '配信中の動画をチェック' : 
                                         status === 'upcoming' ? '配信予定をチェック' : 
                                         'アーカイブをチェック'}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            {/each}
        </div>
    {/if}
</div>

<style>
    [role="button"]:focus-visible {
        @apply outline-none ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-black;
    }
</style> 